import { useState, useEffect } from "react";
import { salonService } from "../../services/salonService";
import { staffService } from "../../services/staffService";
import Icon from "../../components/Common/Icon";
import { useToast } from "../../context/ToastContext";

export default function SalonHierarchyPage() {
  const { showToast } = useToast();

  // Paginated salons state
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [size] = useState(10); // 10 salons per page

  // Filters state
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [city, setCity] = useState("");

  // Tree expansion state
  const [expandedSalons, setExpandedSalons] = useState({});
  const [staffData, setStaffData] = useState({}); // { [salonId]: staffList }
  const [staffLoading, setStaffLoading] = useState({}); // { [salonId]: boolean }

  const loadSalons = (currentPage, currentSearch, currentStatus, currentCity) => {
    setLoading(true);
    const params = {
      page: currentPage,
      size: size,
      search: currentSearch || undefined,
      status: currentStatus || undefined,
      city: currentCity || undefined,
    };

    salonService.list(params)
      .then((res) => {
        const data = res.data?.data;
        setSalons(data?.content ?? []);
        setTotalPages(data?.totalPages ?? 0);
        setTotalElements(data?.totalElements ?? 0);
      })
      .catch((err) => {
        showToast("Salonlar yüklenemedi.", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadSalons(page, search, status, city);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(0);
    loadSalons(0, search, status, city);
  };

  const handleClearFilters = () => {
    setSearch("");
    setStatus("");
    setCity("");
    setPage(0);
    loadSalons(0, "", "", "");
  };

  const toggleSalon = async (salonId) => {
    const isExpanded = !!expandedSalons[salonId];
    
    // Toggle state
    setExpandedSalons(prev => ({
      ...prev,
      [salonId]: !isExpanded
    }));

    // If expanding and staff data is not yet loaded, load it
    if (!isExpanded && !staffData[salonId] && !staffLoading[salonId]) {
      setStaffLoading(prev => ({ ...prev, [salonId]: true }));
      try {
        const res = await staffService.list({ salonId, size: 100 });
        const list = res.data?.data?.content ?? [];
        setStaffData(prev => ({
          ...prev,
          [salonId]: list
        }));
      } catch (err) {
        showToast("Personel bilgileri yüklenemedi.", "error");
      } finally {
        setStaffLoading(prev => ({ ...prev, [salonId]: false }));
      }
    }
  };

  const STATUS_LABELS = {
    ACTIVE: "Aktif",
    SUSPENDED: "Askıda",
    INACTIVE: "Pasif",
  };

  const STATUS_STYLES = {
    ACTIVE: "bg-success/15 text-success border-success/20",
    SUSPENDED: "bg-tier-gold/15 text-tier-gold border-tier-gold/20",
    INACTIVE: "bg-outline-variant/30 text-on-surface-variant border-outline-variant/40",
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Icon name="account_tree" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-on-surface">Salon Yönetimi Hiyerarşisi</h1>
          <p className="text-on-surface-variant text-sm">
            Tüm salon sahiplerini (Manager) ve salonlara bağlı personelleri (Staff) hiyerarşik ağaç yapısında izleyin.
          </p>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <form onSubmit={handleSearchSubmit} className="bg-white rounded-3xl p-6 border border-outline-variant/30 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search Input */}
          <div className="relative">
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Salon Adı</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Salon adı ile ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full border border-outline-variant rounded-xl pl-9 pr-3 py-2 text-xs bg-bg-subtle/50 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
              />
              <Icon name="search" className="absolute left-3 top-2.5 !text-[16px] text-on-surface-variant" />
            </div>
          </div>

          {/* City Input */}
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Şehir</label>
            <input
              type="text"
              placeholder="Şehir adı..."
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-bg-subtle/50 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
            />
          </div>

          {/* Status Dropdown */}
          <div>
            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Durum</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-bg-subtle/50 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
            >
              <option value="">Tümü</option>
              <option value="ACTIVE">Aktif</option>
              <option value="SUSPENDED">Askıda</option>
              <option value="INACTIVE">Pasif</option>
            </select>
          </div>

          {/* Filter Buttons */}
          <div className="flex items-end gap-2">
            <button
              type="submit"
              className="flex-1 bg-primary text-white rounded-xl py-2 font-semibold text-xs hover:opacity-95 transition flex items-center justify-center gap-1.5 h-[34px]"
            >
              <Icon name="filter_alt" className="!text-[16px]" /> Filtrele
            </button>
            {(search || status || city) && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="border border-outline-variant bg-white text-on-surface hover:bg-bg-subtle rounded-xl px-3 py-2 font-semibold text-xs transition h-[34px]"
              >
                Temizle
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Salons list & tree structures */}
      {loading ? (
        <div className="bg-white rounded-3xl p-8 border border-outline-variant/30 text-center text-on-surface-variant">
          <div className="animate-pulse flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
            <p className="text-sm font-semibold">Salonlar ve Hiyerarşi Yükleniyor...</p>
          </div>
        </div>
      ) : salons.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 border border-outline-variant/30 text-center text-on-surface-variant space-y-3">
          <Icon name="storefront" className="!text-[48px] text-on-surface-variant/40" />
          <p className="text-sm font-medium">Aramanıza uygun salon bulunamadı.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-xs text-on-surface-variant font-medium px-1">
            Toplam <strong>{totalElements}</strong> salon listeleniyor.
          </div>

          {salons.map((salon) => {
            const isExpanded = !!expandedSalons[salon.id];
            const salonStaffList = staffData[salon.id] ?? [];
            const isStaffLoading = !!staffLoading[salon.id];

            return (
              <div
                key={salon.id}
                className="bg-white rounded-3xl border border-outline-variant/30 shadow-sm overflow-hidden hover:border-primary/20 transition-all duration-300"
              >
                {/* Salon Card Header Row (Click to toggle expansion) */}
                <div
                  onClick={() => toggleSalon(salon.id)}
                  className="p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:bg-bg-subtle/30 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/5 text-primary flex items-center justify-center flex-shrink-0">
                      <Icon name="spa" className="!text-[22px]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-on-surface text-base flex items-center gap-2">
                        {salon.name}
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATUS_STYLES[salon.status]}`}>
                          {STATUS_LABELS[salon.status] ?? salon.status}
                        </span>
                      </h3>
                      <p className="text-xs text-on-surface-variant mt-0.5">
                        {[salon.district, salon.city].filter(Boolean).join(", ")} &bull; Lisans: <strong className="text-on-surface">{salon.licenseNo}</strong>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 self-stretch sm:self-auto justify-between sm:justify-end border-t sm:border-t-0 pt-3 sm:pt-0 border-outline-variant/30">
                    <div className="text-right text-xs">
                      <p className="text-on-surface-variant">Paket Seviyesi</p>
                      <p className="font-bold text-primary">{salon.packageName ?? "Bronze"}</p>
                    </div>

                    <button className="p-1 rounded-full text-on-surface-variant hover:text-primary hover:bg-primary/5 transition">
                      <Icon
                        name="expand_more"
                        className={`!text-[24px] transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
                      />
                    </button>
                  </div>
                </div>

                {/* Expanded Hierarchy Area */}
                {isExpanded && (
                  <div className="px-6 pb-6 pt-2 border-t border-outline-variant/20 bg-bg-subtle/10">
                    <div className="max-w-2xl mt-3 relative pl-6 border-l border-outline-variant/30 space-y-6">
                      {/* Connection Dot for Root node description */}
                      <div className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-outline-variant"></div>
                      
                      {/* 1. MANAGER Level */}
                      <div className="relative">
                        <div className="absolute -left-[31px] top-4 w-[26px] h-[1px] bg-outline-variant/30"></div>
                        <div className="flex items-center gap-3 bg-white p-3 rounded-2xl border border-outline-variant/30 shadow-sm max-w-md">
                          <div className="w-9 h-9 rounded-xl bg-tier-gold/10 text-tier-gold flex items-center justify-center flex-shrink-0">
                            <Icon name="supervisor_account" className="!text-[20px]" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[11px] font-bold text-tier-gold uppercase tracking-wider">Salon Yöneticisi</p>
                            <h4 className="text-xs font-bold text-on-surface truncate">{salon.ownerName ?? "Bilinmiyor"}</h4>
                          </div>
                        </div>
                      </div>

                      {/* Connection Line from Manager down to Staff list */}
                      <div className="absolute left-[15px] top-12 bottom-6 border-l border-dashed border-outline-variant/50"></div>

                      {/* 2. STAFF Level */}
                      <div className="pl-8 space-y-3">
                        {isStaffLoading ? (
                          <div className="flex items-center gap-2 text-xs text-on-surface-variant py-2">
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                            <span>Personel listesi yükleniyor...</span>
                          </div>
                        ) : salonStaffList.length === 0 ? (
                          <div className="relative flex items-center gap-2 text-xs text-on-surface-variant/70 py-2">
                            <div className="absolute -left-[38px] top-4 w-[34px] border-t border-dashed border-outline-variant/40"></div>
                            <Icon name="info" className="!text-[16px]" />
                            <span>Salonun henüz kayıtlı personeli bulunmuyor.</span>
                          </div>
                        ) : (
                          salonStaffList.map((staff) => (
                            <div key={staff.id} className="relative flex items-center gap-3">
                              {/* Horizontal connector line */}
                              <div className="absolute -left-[38px] top-4.5 w-[34px] border-t border-dashed border-outline-variant/40"></div>

                              <div className="flex items-center justify-between gap-4 bg-white p-2.5 px-4 rounded-xl border border-outline-variant/20 shadow-sm max-w-sm flex-1">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className={`w-2 h-2 rounded-full ${staff.active ? "bg-success" : "bg-outline-variant"}`}></div>
                                  <div className="min-w-0 leading-tight">
                                    <h5 className="text-xs font-bold text-on-surface truncate">{staff.fullName}</h5>
                                    <span className="text-[10px] text-on-surface-variant block truncate">{staff.position ?? "Personel"}</span>
                                  </div>
                                </div>
                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                                  staff.active ? "bg-success/10 text-success" : "bg-outline-variant/30 text-on-surface-variant"
                                }`}>
                                  {staff.active ? "Aktif" : "Pasif"}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-outline-variant/30 pt-4 px-1">
              <span className="text-xs text-on-surface-variant">
                Sayfa <strong>{page + 1}</strong> / <strong>{totalPages}</strong>
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-2 border border-outline-variant rounded-xl bg-white hover:bg-bg-subtle disabled:opacity-40 transition text-on-surface"
                >
                  <Icon name="chevron_left" className="!text-[20px]" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page === totalPages - 1}
                  className="p-2 border border-outline-variant rounded-xl bg-white hover:bg-bg-subtle disabled:opacity-40 transition text-on-surface"
                >
                  <Icon name="chevron_right" className="!text-[20px]" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
